import { isRSAA, RSAA } from 'redux-api-middleware' // RSAA = '@@redux-api-middleware/RSAA'
import config from '../../../config'
import { HUBBLE_API_TOKEN, HUBBLE_MAINTENANCE_TOKEN, PURGATORY_API_TOKEN, PURGATORY_MAINTENANCE_TOKEN } from '../APIUrl'
import { SecurityUtils } from '../../../utils'

const {
    authExceptions,
    sso: { url: SSOURL },
    auth0: { enabled: AUTH0_ENABLED },
} = config

const logger = console

const HUBBLE_NAMESPACE = '/hubble'
const MAINTENANCE_NAMESPACE = '/maintenance'

/**
 * Build a list with all the hosts that need authentication
 * @param {Object} state  Redux state
 */
const buildAuthList = (state = {}) => {
    const { app } = state
    const { estates = [] } = app || {}
    const authList = [...estates.map(({ url }) => url), SSOURL, ]
    return authList
}

/**
 * 
 * @param {string} url
 * @returns {boolean}
 */
const isUsingHubbleNamespace = (url) => {
    const hints = [
        HUBBLE_API_TOKEN,
        PURGATORY_API_TOKEN,
        HUBBLE_NAMESPACE,
    ]

    return hints.some(namespace => url.includes(namespace))
}

/**
 * 
 * @param {string} url
 * @returns {boolean}
 */
const isUsingMaintenanceNamespace = (url) => {
    const hints = [
        HUBBLE_MAINTENANCE_TOKEN,
        PURGATORY_MAINTENANCE_TOKEN,
        MAINTENANCE_NAMESPACE,
    ]

    return hints.some(namespace => url.includes(namespace))
}

/**
 * Checks if an API needs authentication by comparing its url
 * with a list of hosts
 * @param {String} url API's url
 * @param {Object} state Redux state
 */
const needsAuth = (url, state = {}) => {
    if (isUsingHubbleNamespace(url) || isUsingMaintenanceNamespace(url)) {
        return true
    }

    const urlLower = url.toLowerCase()
    // Hosts that needs authentication check
    const authList = buildAuthList(state)
    return authList.map((site) => urlLower.includes(site.toLowerCase())).reduce((a, b) => a || b, false)
}

/**
 * Checks if an API is an exception for authentication
 * @param {String} url API's url
 * @param {String} method Request method
 */
const isAuthException = (url = '', method = '') => {
    const methodUpper = method.toUpperCase()
    const urlLower = url.toLowerCase()
    return authExceptions
        .map(({ method: excpMethod, url: excpUrl }) => urlLower.includes(excpUrl.toLowerCase()) && methodUpper === excpMethod.toUpperCase())
        .reduce((a, b) => a || b, false)
}

/**
 * API Auth middleware for Redux
 * @param {Object} store
 */
const auth = (store) => (next) => (action) => {
    let nextAction = action

    try {
        if (isRSAA(action)) {
            const {
                [RSAA]: { endpoint, headers, method, body },
            } = action
            const state = store.getState()

            // If the endpoint we're calling needs authentication
            if (needsAuth(endpoint, state) && !isAuthException(endpoint, method)) {
                const { user } = state || {}
                const { session = {} } = user || {}
                let authHeaders = {}

                // Authentication depends if auth0 is enabled
                if (AUTH0_ENABLED) {
                    if (isUsingHubbleNamespace(endpoint)) {
                        const { accessToken } = session
                        authHeaders = {
                            Authorization: `Bearer ${accessToken}`,
                        }
                    } else if (isUsingMaintenanceNamespace(endpoint)) {
                        const {
                            key,
                            signature,
                            timestamp,
                        } = SecurityUtils.generateAppCredentials(body)
                        authHeaders = {
                            signature,
                            'x-vol-stamp': timestamp,
                            'x-vol-key': key,
                        }
                    }
                } else {
                    const { sessionId: vssosessionid } = session
                    authHeaders = {
                        vssosessionid,
                    }
                }

                nextAction = {
                    ...action,
                    [RSAA]: {
                        ...action[RSAA],
                        // Append auth headers
                        headers: {
                            ...headers,
                            ...authHeaders,
                        },
                    },
                }
            }
        }
    } catch (error) {
        logger.error(error)
    }

    return next(nextAction)
}

export default auth
